import * as React from "react";
import { DEFAULT_ORGANIZATION_POLICY, type OrganizationPolicy } from "@opus-dt/shared-types";
import { useOrganization } from "@/features/organization/hooks";

// Spec 079 (FR-030) — A POLÍTICA DE JULGAMENTO no cliente.
//
// Os limiares que pintam o painel (verde/amarelo do OEE) e os que julgam a aderência ("atrasado",
// "no plano", "adiantado") eram constantes de módulo — `85`, `65`, `95`, `105` escritos em
// `oee-thresholds.ts` e `plan-adherence-verdict.ts`. Passam a vir da organização.
//
// ⚠️ CONTEXTO, e NÃO um `useQuery` por consumidor. Duas razões, e a segunda foi descoberta
// quebrando:
//   1. Uma leitura para o app inteiro. Um `useQuery` dentro de `OeeBadge` significaria um hook de
//      query por crachá — o cache do TanStack os dedupla, mas cada um vira um assinante a mais.
//   2. `useQuery` LANÇA fora de um `QueryClientProvider`. Como os limiares são lidos por primitivos
//      folha (o dot da métrica, a barra do breakdown, o medidor de ritmo da cena 3D), isso obrigaria
//      TODO teste que renderiza qualquer um deles a montar um provider — 64 testes quebraram
//      exatamente assim. Um contexto com valor padrão não lança: quem não tem provider recebe os
//      padrões DA COLUNA, que são os mesmos literais que esta spec removeu do código.
//
// ⚠️ O padrão também vale enquanto a query carrega, e é de propósito: o painel pinta certo desde o
// primeiro frame, e a troca para o valor da fábrica é invisível quando eles coincidem (o caso
// comum). Esperar a query para pintar trocaria uma cor eventualmente diferente por um painel cinza
// a cada navegação.

const OrganizationPolicyContext = React.createContext<OrganizationPolicy>(
  DEFAULT_ORGANIZATION_POLICY
);

/**
 * Montado UMA vez, no `ProtectedLayout` — e NÃO no `AppLayout`, onde vive
 * `useRealtimeInvalidate`: a rota ambiente com sessão (`/lines/:id/ambient`) fica fora do
 * `AppLayout` e pinta os mesmos indicadores.
 *
 * ⚠️ A rota ambiente PÚBLICA (`/display/:token`) fica fora até do `ProtectedLayout` e, portanto,
 * fora deste provider — ela cai nos padrões. Não é corrigível movendo o provider (a rota não tem
 * sessão, e `GET /organization` exige token): os limiares precisam viajar na resposta de display.
 */
export function OrganizationPolicyProvider({ children }: { children: React.ReactNode }) {
  const { data } = useOrganization();
  // ⚠️ SEM `useMemo`, e é correto: o TanStack Query v5 faz STRUCTURAL SHARING por padrão — um
  // refetch cujo resultado é igual devolve a MESMA referência. Memoizar sobre os dez valores só
  // reimplementaria, com uma lista de dependências para manter à mão, o que a biblioteca já
  // garante. E `DEFAULT_ORGANIZATION_POLICY` é constante de módulo, então o caminho do fallback
  // também é referencialmente estável.
  const value = data ?? DEFAULT_ORGANIZATION_POLICY;
  return (
    <OrganizationPolicyContext.Provider value={value}>
      {children}
    </OrganizationPolicyContext.Provider>
  );
}

export function useOrganizationPolicy(): OrganizationPolicy {
  return React.useContext(OrganizationPolicyContext);
}

/** O par que decide a COR de um percentual de OEE. */
export interface OeeThresholds {
  good: number;
  warn: number;
}

export function useOeeThresholds(): OeeThresholds {
  const policy = useOrganizationPolicy();
  return { good: policy.oeeThresholdGood, warn: policy.oeeThresholdWarn };
}

/** O par que decide o VEREDITO de uma run em andamento. */
export interface PlanAdherenceBounds {
  low: number;
  high: number;
}

export function usePlanAdherenceBounds(): PlanAdherenceBounds {
  const policy = useOrganizationPolicy();
  return { low: policy.planAdherenceLow, high: policy.planAdherenceHigh };
}
